import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import numeral from 'numeral';

import { DEBOUNCE_TIMEOUT } from 'constants/Global';

/**
 * Clean value for being use in regex.
 * @param {String} value Value to clean
 */
const cleanRegex = (value) => String(value).replace(/[\]/\\^$*+?.(){}|[-]/gim, '');

/**
 * Bounded logic used for Counter. Counter cannot decrease below minimum and maximum is suffixed with a plus when reached.
 * @param {Number} min Minimum value
 * @param {Number} max Maximum value
 */
export const BoundedLogic = (min, max) => {
    const validRegex = new RegExp(`^${min < 0 ? '-?' : ''}[0-9]*\\+?$`);

    return {
        isValid: (value) => {
            validRegex.lastIndex = 0;
            return validRegex.test(value);
        },
        toCount: (value) => {
            const count = numeral(value).value();

            if (count > max) {
                return max;
            }
            if (count === null) {
                return 0;
            }
            if (count <= min) {
                return min;
            }
            return count;
        },
        toValue: (count) => {
            if (count >= max) {
                return `${max}+`;
            }
            if (count <= min) {
                return String(min);
            }
            return String(count);
        },
    };
};

/**
 * Zero value logic for Counter, when counter go down to zero, the given value is displayed.
 * @param {String} zeroValue Value displayed at zero (not escape)
 */
export const ZeroValueLogic = (zeroValue) => {
    const validRegex = new RegExp(`^[${cleanRegex(zeroValue).toLowerCase()}0-9]{0,3}\\+?$`, 'i');

    return {
        isValid: (value) => {
            validRegex.lastIndex = 0;
            return validRegex.test(value);
        },
        toCount: (value) => {
            const count = numeral(value).value();

            if (count === null || count < 1) {
                return 0;
            }
            return count;
        },
        toValue: (count) => {
            if (count < 1) {
                return zeroValue;
            }
            return String(count);
        },
    };
};

/**
 * List value logic for Counter, item should be in the given list.
 * @param {Array} list Value displayed at zero (not escape)
 */
export const ListLogic = (list) => ({
    isValid: (value) => list.find((item) => value === item.value),
    toCount: (value) => {
        const index = numeral(value).value();
        if (index === null || index < 1) {
            return list[0].value;
        }
        if (index > list.length - 1) {
            return list[list.length - 1].value;
        }
        return list[index].value;
    },
    toValue: (count) => {
        if (count < 1) {
            return list[0].label;
        }
        return list[count].label;
    },
});

/**
 * Counter.
 */
export default class Counter extends Component {
    /**
     * React lifecycle.
     * @param {Object} props Initial props
     */
    constructor(props) {
        super(props);

        /**
         * React properties.
         * Storing displayed value to user.
         */
        this.state = {
            value: this.props.fieldLogic.toValue(this.props.input.value),
        };

        this.onChange = this.onChange.bind(this);
        this.update = this.update.bind(this);
        this.countDown = this.countDown.bind(this);
        this.countUp = this.countUp.bind(this);
    }

    /**
     * React lifecycle.
     * Update value displayed if real value has changed.
     * @param {Object} prevProps Prev props of component
     */
    componentDidUpdate(prevProps) {
        const { fieldLogic, input } = this.props;

        if (prevProps.input.value !== input.value) {
            input.onBlur(input.value);

            const value = fieldLogic.toValue(input.value);

            if (
                (this.state.value === '' && value !== fieldLogic.toValue(0)) ||
                (this.state.value !== '' && this.state.value !== value)
            ) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ value });
            }
        }
    }

    /**
     * onChange handler when user type into field.
     * @param {String} rawValue Input value
     */
    onChange(rawValue) {
        if (this.props.fieldLogic.isValid(rawValue)) {
            clearTimeout(this.onChangeTimeout);
            this.setState({ value: rawValue });

            /**
             * Storing timeout for canceling it while debouncing.
             */
            this.onChangeTimeout = setTimeout(() => this.update(rawValue), DEBOUNCE_TIMEOUT);
        }
    }

    /**
     * Change count and value from given input (count or value).
     * @param {Number} raw New count or value
     */
    update(raw) {
        const count = this.props.fieldLogic.toCount(String(raw));
        const value = raw === '' ? raw : this.props.fieldLogic.toValue(count);

        this.setState({ value });
        this.props.input.onChange(count);
    }

    /**
     * Decrease counter by 1.
     */
    countDown() {
        this.update(this.props.input.value - 1);
    }

    /**
     * Increase counter by 1.
     */
    countUp() {
        this.update(this.props.input.value + 1);
    }

    /**
     * React lifecycle.
     */
    render() {
        const {
            input,
            small,
            meta: { active, touched, visited, error },
        } = this.props;

        const isError = touched || visited;

        const wrapperClasses = classnames({
            field: true,
            'field--counter': true,
            'field--small': small,
            'field--active': active,
            'field--invalid': isError && error,
        });

        const inputClasses = classnames({
            field__control: true,
            'field__control--counter': !small,
            'field__control--small-counter': small,
        });

        return (
            <div className="field-container">
                <div className={wrapperClasses}>
                    <input
                        className={inputClasses}
                        id={input.name}
                        type="text"
                        onFocus={input.onFocus}
                        onChange={(e) => this.onChange(e.target.value)}
                        onBlur={() => input.onBlur(input.value)}
                        value={this.state.value}
                    />
                    <button
                        tabIndex="-1"
                        className="field__count field__count--down"
                        type="button"
                        onClick={this.countDown}
                    >
                        <span className="icon-remove field__icon" />
                    </button>
                    <button
                        tabIndex="-1"
                        className="field__count field__count--up"
                        type="button"
                        onClick={this.countUp}
                    >
                        <span className="icon-add field__icon" />
                    </button>
                </div>
                {isError && error && <p className="field-error">{error}</p>}
            </div>
        );
    }
}

Counter.displayName = 'Counter';

Counter.propTypes = {
    fieldLogic: PropTypes.shape({}),
    input: PropTypes.shape({
        name: PropTypes.string,
        onChange: PropTypes.func.isRequired,
        onBlur: PropTypes.func.isRequired,
        value: PropTypes.number.isRequired,
    }).isRequired,
    small: PropTypes.bool,
    meta: PropTypes.shape({
        error: PropTypes.string,
        touched: PropTypes.bool,
        visited: PropTypes.bool,
    }),
};

Counter.defaultProps = {
    fieldLogic: {
        isValid: (value) => /^-?[0-9]*$/.test(value),
        toCount: (value) => numeral(value).value(),
        toValue: (count) => String(count),
    },
    small: false,
};
